import { useEffect, useState } from "react";

/**
 * Owns the "once per calendar day" chained-modal state for the Dashboard:
 *   1. Urgent Issues Checklist pops on first login of the day.
 *   2. Daily AI Briefing follows once the coach dismisses the urgent modal.
 *
 * Split out of App.js so the daily-modal orchestration is testable in
 * isolation and the Dashboard component stops accreting effects.
 *
 * @param {string|null|undefined} userEmail  used to scope the last-seen keys
 * @returns {{
 *   urgentOpen: boolean,
 *   briefingOpen: boolean,
 *   openUrgent: () => void,
 *   closeUrgent: () => void,
 *   openBriefing: () => void,
 *   closeBriefing: () => void,
 * }}
 */
export default function useDailyModals(userEmail) {
  const [urgentOpen, setUrgentOpen] = useState(false);
  const [briefingOpen, setBriefingOpen] = useState(false);
  const [briefingPending, setBriefingPending] = useState(false);

  useEffect(() => {
    if (!userEmail) return;
    const todayKey = new Date().toISOString().slice(0, 10);
    const lastSeenKey = `ych_urgent_last_seen:${userEmail}`;
    const lastSeen = window.localStorage.getItem(lastSeenKey);
    if (lastSeen !== todayKey) {
      setUrgentOpen(true);
      window.localStorage.setItem(lastSeenKey, todayKey);
    }
    const briefKey = `ych_briefing_last_seen:${userEmail}`;
    const lastBrief = window.localStorage.getItem(briefKey);
    if (lastBrief !== todayKey) {
      setBriefingPending(true);
      window.localStorage.setItem(briefKey, todayKey);
    }
  }, [userEmail]);

  // Chain the two daily modals — briefing opens when urgent closes.
  useEffect(() => {
    if (briefingPending && !urgentOpen) {
      setBriefingOpen(true);
      setBriefingPending(false);
    }
  }, [briefingPending, urgentOpen]);

  return {
    urgentOpen,
    briefingOpen,
    openUrgent: () => setUrgentOpen(true),
    closeUrgent: () => setUrgentOpen(false),
    openBriefing: () => setBriefingOpen(true),
    closeBriefing: () => setBriefingOpen(false),
  };
}
